import { motion } from 'framer-motion'
import {
  WrenchScrewdriverIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  MapIcon,
  CloudIcon,
  CubeIcon,
} from '@heroicons/react/24/solid';

// Enhanced Glassy Icons Component with better responsiveness
const GlassyIcon = ({ children, gradient, glowColor }) => (
  <div className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl overflow-hidden group-hover:scale-110 transition-all duration-500 mx-auto">
    {/* Main glass background */}
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-90`} />
    
    {/* Glass reflection effect */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent" />
    
    {/* Inner glow */}
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} blur-sm opacity-50`} />
    
    {/* Outer glow on hover */}
    <div className={`absolute -inset-1 sm:-inset-2 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-30 blur-xl transition-all duration-500`} />
    
    {/* Border highlight */}
    <div className="absolute inset-0 border border-white/20 rounded-xl sm:rounded-2xl" />
    
    {/* Icon container */}
    <div className="relative w-full h-full flex items-center justify-center text-white drop-shadow-lg">
      <div className="transform scale-75 sm:scale-90 md:scale-100">
        {children}
      </div>
    </div>
  </div>
)

const services = [
  {
    icon: <WrenchScrewdriverIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Construction & Site Management",
    description: "We have extensive experience in civil works as a civil Engineering company. For civil engineering projects, contact JTG Enterprises Limited for quality projects and durable structures including buildings, roads and prominent civil infrastructure in Kenya.",
    gradient: "from-amber-400 via-orange-500 to-red-500",
    glowColor: "amber",
    category: "Construction"
  },
  {
    icon: <TruckIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Road Construction & Maintenance",
    description: "JTG Enterprises Limited has constructed numerous roads in Kenya and the region. We use quality construction materials and provide comprehensive road construction solutions guaranteeing durable roads with excellent service life.",
    gradient: "from-blue-400 via-indigo-500 to-purple-600",
    glowColor: "blue",
    category: "Infrastructure"
  },
  {
    icon: <ClipboardDocumentCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Water & Sewer Systems",
    description: "We specialize in water works and storm and waste water management projects. We have undertaken major projects including the Thwake Water Multipurpose Development Project – Main Spill way construction.",
    gradient: "from-cyan-400 via-blue-500 to-indigo-600",
    glowColor: "cyan",
    category: "Water Works"
  },
  {
    icon: <MapIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Plant & Machinery",
    description: "We offer comprehensive plant and machinery solutions for hire and leasing, ensuring you have access to the right equipment for your construction needs with professional maintenance support.",
    gradient: "from-green-400 via-emerald-500 to-teal-600",
    glowColor: "green",
    category: "Equipment"
  },
  {
    icon: <CloudIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Building Materials Supply",
    description: "We provide a comprehensive range of high-quality building and construction materials to support your project needs, ensuring consistent supply chain management.",
    gradient: "from-purple-400 via-violet-500 to-indigo-600",
    glowColor: "purple",
    category: "Supply"
  },
  {
    icon: <CubeIcon className="h-5 w-5 sm:h-6 sm:w-6" />,
    title: "Stainless Steel Fabrication",
    description: "JTG Enterprises Limited has extensive experience in stainless steel fabrications. We offer complete fabrication services from design to manufacture and installation for mild steel, stainless steel, and aluminium projects.",
    gradient: "from-rose-400 via-pink-500 to-red-500",
    glowColor: "rose",
    category: "Fabrication"
  }
];

const Services = () => {
  return (
    <section id="services" className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Animated gradient blobs */}
        <div className="absolute top-1/4 left-1/6 w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-[#1a4aff]/20 to-[#38BDF8]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/3 right-1/6 w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 bg-gradient-to-br from-[#38BDF8]/15 to-[#1a4aff]/15 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-2xl animate-pulse" />
        
        {/* Enhanced grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(26,74,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(26,74,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px] sm:bg-[size:30px_30px] lg:bg-[size:40px_40px] opacity-30" />
        
        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.015] mix-blend-soft-light bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E')]" />
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-4 sm:mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-[#38BDF8]/10 backdrop-blur-sm border border-[#38BDF8]/20 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-[#38BDF8] mb-4 sm:mb-6">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#38BDF8] rounded-full animate-pulse" />
              Our Expertise
            </div>
          </motion.div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
            Professional
            <span className="block bg-gradient-to-r from-[#38BDF8] via-[#1a4aff] to-[#38BDF8] bg-clip-text text-transparent animate-gradient">
              Services
            </span>
          </h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base sm:text-lg lg:text-xl text-white/80 max-w-4xl mx-auto leading-relaxed px-4"
          >
            Delivering excellence through innovative engineering solutions and unmatched expertise in construction, water works, and infrastructure development across Kenya and the region.
          </motion.p>
        </motion.div>

        {/* Enhanced Services Grid with improved responsiveness */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.1,
                ease: "easeOut"
              }}
              className="group relative h-full"
            >
              {/* Enhanced glow effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-20 rounded-2xl sm:rounded-3xl blur-sm transition-all duration-500`} />
              
              <div className="relative bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 h-full hover:border-[#38BDF8]/30 transition-all duration-500 hover:shadow-[0_10px_40px_0_rgba(26,74,255,0.2)] group-hover:bg-slate-800/90">
                <div className="flex flex-col h-full">
                  {/* Category badge */}
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <span className="inline-block bg-slate-700/50 text-slate-300 text-xs px-2 py-1 rounded-full border border-slate-600/30">
                      {service.category}
                    </span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l10 10M17 7l-10 10" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Enhanced Glassy Icon */}
                  <motion.div
                    whileHover={{ rotate: 3, scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 mb-4 sm:mb-6"
                  >
                    <GlassyIcon gradient={service.gradient} glowColor={service.glowColor}>
                      {service.icon}
                    </GlassyIcon>
                  </motion.div>
                  
                  {/* Content */}
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-[#38BDF8] transition-colors duration-300 leading-tight">
                      {service.title}
                    </h3>
                    
                    <p className="text-sm sm:text-base text-slate-300 group-hover:text-slate-200 transition-colors duration-300 leading-relaxed mb-4 sm:mb-6 flex-1">
                      {service.description}
                    </p>
                    
                    {/* Enhanced call-to-action */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.05 }}
                      className="mt-auto"
                    >
                      <a 
                        href="#contact" 
                        className="inline-flex items-center gap-2 text-[#38BDF8] text-sm font-medium tracking-wide opacity-0 group-hover:opacity-100 transition-all duration-300 hover:gap-3 relative"
                      >
                        <span className="relative">
                          Learn More
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#38BDF8] group-hover:w-full transition-all duration-300"></span>
                        </span>
                        <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enhanced bottom section with better mobile layout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative"
        >
          <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 max-w-6xl mx-auto relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 bg-gradient-to-bl from-[#38BDF8]/10 to-transparent rounded-full blur-2xl" />
            
            <motion.div
              initial={{ scale: 0.95 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-center relative z-10"
            >
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
                Why Choose Our Services?
              </h3>
              <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed mb-8 sm:mb-12 max-w-4xl mx-auto">
                Our comprehensive range of services is designed to meet the diverse needs of our clients,
                from small-scale projects to large infrastructure developments. We combine technical expertise
                with innovative solutions to deliver exceptional results that exceed expectations.
              </p>
              
              {/* Enhanced Stats Row with better mobile layout */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
                {[
                  { number: "200+", label: "Projects Completed" },
                  { number: "15+", label: "Years of Excellence" },
                  { number: "50+", label: "Expert Team Members" },
                  { number: "100%", label: "Client Satisfaction" }
                ].map((stat, index) => (
                  <motion.div 
                    key={index} 
                    className="text-center p-3 sm:p-4 rounded-xl bg-slate-700/30 border border-slate-600/30 hover:border-[#38BDF8]/30 transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-[#38BDF8] to-[#1a4aff] bg-clip-text text-transparent mb-1 sm:mb-2">
                      {stat.number}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
              
              {/* Enhanced CTA button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
              >
                <a href="#contact">
                  <button className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#1a4aff] to-[#38BDF8] text-white text-sm sm:text-base font-semibold rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-[#1a4aff]/25 hover:-translate-y-1 active:translate-y-0 active:shadow-lg">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#38BDF8] to-[#1a4aff] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span className="relative flex items-center justify-center gap-2">
                      Get Started Today
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced animations and styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite 2s;
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 4s ease-in-out infinite;
        }
        
        /* Custom scrollbar for better UX */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(71, 85, 105, 0.1);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(56, 189, 248, 0.3);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(56, 189, 248, 0.5);
        }
      `}</style>
    </section>
  )
}

export default Services